import {
  IDENTITY_VISUAL_INSET_RATIOS,
  type VisualInsetRatios,
  visualToGeometricRect,
} from '@/lib/shapes/shape-visual-bounds'
import { localVecToCanvas } from '@/lib/transforms/geometry'
import { canvasDeltaToMultiResizeLocal } from '@/lib/transforms/multi-select-chrome-resize'
import type { UnifiedSelectionResizeHandle } from '@/lib/transforms/types/unified-selection-resize-handle'
import { computeUnifiedSelectionOrientedResizeLocal } from '@/lib/transforms/unified-selection-oriented-resize'
import type { OrientedRect } from './types/oriented-rect'

/**
 * Resize a single axis-aligned placement rect in canvas space, accounting for `rotation`
 * (degrees). Matches shape-manager / unified selection oriented resize behavior.
 *
 * `initial` is — and the returned rect is — the shape's **geometric** rect, the generator box that
 * gets persisted. But the handle the user grabbed is drawn on the shape's **visual** box
 * (`selectionChromeFrameOfShape` → `drawableRectOfShape`), which for anything that inscribes its
 * vertices in the geometric box's ellipse (every regular polygon and star) is strictly smaller. Pass
 * `ratios` to reconcile the two: the drag is applied to the visual box and the result converted back
 * through {@link visualToGeometricRect}, so the visual edge tracks the pointer 1:1.
 *
 * Without `ratios` the geometric box absorbs the raw delta and the visual edge only moves by `span`
 * times as far — a triangle dragged from its south handle (`spanY = 0.75`) trails the cursor by 25%.
 * The default is identity, which reduces this to the pre-`ratios` behavior *exactly* (no float
 * drift: identity divides by 1 and subtracts 0), so a rectangle — and every existing caller that
 * doesn't pass ratios — is bit-for-bit unchanged.
 *
 * Two properties worth not breaking:
 *
 * - **Rotation still pivots on the geometric center.** The local frame stays anchored at
 *   `(-w0/2, -h0/2)` and only the *rect inside it* becomes the visual box, so `localVecToCanvas`
 *   below still rotates about the geometric center per the `rotationPivotOfShape` invariant. Passing
 *   the visual rect as if it were the whole box instead would pivot on the *visual* center — for a
 *   triangle that is `0.375h`, not `0.5h`, and a rotated one would drift on every drag tick.
 * - **Aspect lock is unaffected.** `visual aspect = (spanX/spanY) × geometric aspect` and
 *   `spanX/spanY` is fixed per shape configuration, so locking the visual aspect ratio *is* locking
 *   the geometric one.
 *
 * One deliberate semantic shift: `minDimension` now floors the **visual** extent rather than the
 * geometric one, making the effective geometric floor `minDimension / span`. That is the better end
 * to clamp — the floor exists so an object can't be dragged down to an ungrabbable hairline, and
 * what the user sees and grabs is the visual box.
 */
export function computeSingleObjectOrientedResize(
  initial: OrientedRect,
  direction: UnifiedSelectionResizeHandle,
  canvasDx: number,
  canvasDy: number,
  shiftKey: boolean,
  aspectLock: { current?: 'width' | 'height' },
  panelDimensionsLinked: boolean,
  minDimension: number,
  /** See `shouldUseAspectRatio` in unified-selection-oriented-resize.ts — forces aspect lock on a
   *  corner drag regardless of Shift, matching legacy's own photo-fill resize behavior. */
  hasPhotoFill = false,
  /** The shape's visual-inset ratios (`visualInsetRatiosForShape`). Identity for a rectangle. */
  ratios: VisualInsetRatios = IDENTITY_VISUAL_INSET_RATIOS
): { next: { x: number; y: number; width: number; height: number }; useAspectRatio: boolean } {
  const w0 = initial.width ?? 0
  const h0 = initial.height ?? 0
  const rot = initial.rotation ?? 0
  const { x: localDx, y: localDy } = canvasDeltaToMultiResizeLocal(canvasDx, canvasDy, rot)
  // The visual box, expressed in the frame centered on the GEOMETRIC center — see the doc comment.
  const localInitial = {
    x: -w0 / 2 + ratios.rx0 * w0,
    y: -h0 / 2 + ratios.ry0 * h0,
    width: (ratios.rx1 - ratios.rx0) * w0,
    height: (ratios.ry1 - ratios.ry0) * h0,
  }
  const { next: nextVisualLocal, useAspectRatio } = computeUnifiedSelectionOrientedResizeLocal({
    direction,
    initial: localInitial,
    dx: localDx,
    dy: localDy,
    shiftKey,
    panelDimensionsLinked,
    minDimension,
    aspectLock,
    hasPhotoFill,
  })
  // Back to the generator rect, still center-relative.
  const nextLocal = visualToGeometricRect(nextVisualLocal, ratios)

  const initialCenter = {
    x: initial.x + w0 / 2,
    y: initial.y + h0 / 2,
  }
  const centerLocal = {
    x: nextLocal.x + nextLocal.width / 2,
    y: nextLocal.y + nextLocal.height / 2,
  }
  const centerOffset = localVecToCanvas(centerLocal, rot)
  const nextCenter = {
    x: initialCenter.x + centerOffset.x,
    y: initialCenter.y + centerOffset.y,
  }

  return {
    next: {
      x: nextCenter.x - nextLocal.width / 2,
      y: nextCenter.y - nextLocal.height / 2,
      width: nextLocal.width,
      height: nextLocal.height,
    },
    useAspectRatio,
  }
}
